import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { GanttCachingService } from '../../gantt/services/ganttCaching.service';
import { GanttColumnSize } from '../../gantt/utilities/gantt-column-size';
import { WeatherDataCachingService } from './weather-data/weather-data-caching.service';
import { WeatherDataCache } from '../models/weatherDataCache';
import { WeatherDataService } from './weather-data.service';

let inMemoryStorage: object = {};

@Injectable({
  providedIn: 'root',
})
export class WtStorageService {
  private isNativePlatform: boolean;

  constructor() {
    this.isNativePlatform = Capacitor.getPlatform() !== 'web';
  }

  getItem(key: string): string | null {
    if (this.isNativePlatform) {
      const value = inMemoryStorage[key];
      console.log(`WtStorageService.getItem [NATIVE] - Key: "${key}", Value:`, value);
      return value;
    } else {
      const value = localStorage.getItem(key);
      console.log(`WtStorageService.getItem [WEB] - Key: "${key}", Value:`, value);
      return value;
    }
  }

  removeItem(key: string): void {
    if (this.isNativePlatform) {
      console.log(`WtStorageService.removeItem [NATIVE] - Key: "${key}"`);
      delete inMemoryStorage[key];
      void Preferences.remove({ key: key });
    } else {
      console.log(`WtStorageService.removeItem [WEB] - Key: "${key}"`);
      localStorage.removeItem(key);
      console.log(`WtStorageService.removeItem [WEB] - Successfully removed from localStorage`);
    }
  }

  setItem(key: string, data: string): void {
    if (this.isNativePlatform) {
      console.log(`WtStorageService.setItem [NATIVE] - Key: "${key}", Value:`, data);
      inMemoryStorage[key] = data;
      console.log(`WtStorageService.setItem [NATIVE] - Stored in memory cache`);

      // Store to Capacitor Preferences (async, but we track completion)
      Preferences.set({ key: key, value: data })
        .then(() => {
          console.log(`WtStorageService.setItem [NATIVE] - Successfully stored to Capacitor Preferences: "${key}"`);
        })
        .catch((error) => {
          console.error(`WtStorageService.setItem [NATIVE] - ERROR storing to Capacitor Preferences: "${key}"`, error);
        });
    } else {
      console.log(`WtStorageService.setItem [WEB] - Key: "${key}", Value:`, data);
      localStorage.setItem(key, data);
      console.log(`WtStorageService.setItem [WEB] - Successfully stored in localStorage`);
    }
  }

  clear(): void {
    if (this.isNativePlatform) {
      inMemoryStorage = {};
      void Preferences.clear();
    }

    //We want to keep the dashboard layout, gantt cache and weather data cache
    const dashboardLayout = JSON.parse(localStorage.getItem('dashboardLayout')) as unknown;
    const ganttColumnSizesCache = JSON.parse(localStorage.getItem(GanttCachingService.gridColumnsKey)) as GanttColumnSize[];
    const ganttLayoutViewsTreeNodesCache = JSON.parse(localStorage.getItem(GanttCachingService.viewsExpandedTreeNodesKey)) as unknown;
    const weatherDataCache = JSON.parse(localStorage.getItem(WeatherDataCachingService.localStorageKey)) as WeatherDataCache[];

    localStorage.clear();

    if (dashboardLayout) {
      this.setItem('dashboardLayout', JSON.stringify(dashboardLayout));
    }

    if(ganttColumnSizesCache) {
      this.setItem(GanttCachingService.gridColumnsKey, JSON.stringify(ganttColumnSizesCache));
    }

    if(ganttLayoutViewsTreeNodesCache) {
      this.setItem(GanttCachingService.viewsExpandedTreeNodesKey, JSON.stringify(ganttLayoutViewsTreeNodesCache));
    }

    if(weatherDataCache) {
      //We want to remove the weather data cache that is older than the predefined expiration time
      const nowTimestamp = new Date().getTime();
      const filteredWeatherDataCache = weatherDataCache.filter((d) => nowTimestamp - d.timestamp < WeatherDataService.cacheExpireTime);

      this.setItem(WeatherDataCachingService.localStorageKey, JSON.stringify(filteredWeatherDataCache));
    }
  }

  /**
   * Because till this moment OAuthStorage works it synchronous data , we need to load the storage in memory
   */
  async loadAllDataInMemoryFromStorage() {
    console.log('WtStorageService.loadAllDataInMemoryFromStorage - Starting to load from Capacitor Preferences...');
    const keysObj = await Preferences.keys();
    console.log('WtStorageService.loadAllDataInMemoryFromStorage - Found keys:', keysObj.keys);

    for (const key of keysObj.keys) {
      await Preferences.get({ key }).then((res) => {
        inMemoryStorage[key] = res.value;
        console.log(`WtStorageService.loadAllDataInMemoryFromStorage - Loaded "${key}":`, res.value);
      });
    }

    console.log('WtStorageService.loadAllDataInMemoryFromStorage - Completed. In-memory storage:', inMemoryStorage);
  }
}
